import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Option,Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  CheckboxStateValue,
  MultiCheckbox,
} from "@admin/components/atoms/MultiStateCheckbox/MultiCheckbox";
import { GoodSelection } from "@admin/components/molecules/SelectWithAdd/GoodSelection";
import { SupplierSelection } from "@admin/components/molecules/SelectWithAdd/SupplierSelection";
import {
  AcceptenceCriteriaColors,
  addItem,
  answerMulticheckValues,
  FoodControlPointInstance,
  goodsTemperaturesCool,
  goodsTemperaturesDryFood,
  goodsTemperaturesFish,
  goodsTemperaturesFrost,
  InstanceStatus,
  Item,
  parseRemarkAndCorrectiveActions,
  Tempature,
  updateItem,
  UpsertModalType,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation,useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { SupplierModalAction } from "../MyLists/SuppliersTable";
import { UpsertAcceptanceProductModal } from "./UpsertAcceptanceProductModal";
import { UpsertSupplierModal } from "./UpsertSupplierModal";

interface FoodAcceptanceModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  initialData?: Item;
}

export function FoodAcceptanceModal({
  foodControlPointInstance,
  initialData,
  ...props
}: FoodAcceptanceModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isCorrectiveActionNeeded, setIsCorrectiveActionNeeded] =
    useState(false);
  const [typeOfProduct, setTypeOfProduct] = useState("");
  const [isChekmarksOk, setIsCheckMarksOk] = useState(false);
  const [isRemarksNeeded, setIsRemarksNeeded] = useState(false);
  const [note, setNote] = useState<string[]>([]);
  const [selectedTemperature, setSelectedTemperature] = useState<
    Option | undefined
  >();
  const [temperatureOptions, setTemperatureOptions] = useState<Tempature[]>([]);
  const numberOfCorrectiveActionSelected = useRef(0);
  const [checkBoxStates, setCheckBoxStates] = useState<CheckboxStateValue[]>(
    [],
  );

  const checkBoxData: CheckboxStateValue[] = useMemo(
    () => [
      {
        value: initialData?.validateMarking
          ? answerMulticheckValues(initialData.validateMarking)
          : "yes",
        stateName: "ValidateMarking",
      },
      {
        value: initialData?.validateExpirety
          ? answerMulticheckValues(initialData.validateExpirety)
          : "yes",
        stateName: "ValidateExpirety",
      },
      {
        value: initialData?.validatePackaging
          ? answerMulticheckValues(initialData.validatePackaging)
          : "yes",
        stateName: "ValidatePackaging",
      },
      {
        value: initialData?.validateQuality
          ? answerMulticheckValues(initialData.validateQuality)
          : "yes",
        stateName: "ValidateQuality",
      },
      {
        value: initialData?.validateTemperature
          ? answerMulticheckValues(initialData.validateTemperature)
          : "yes",
        stateName: "ValidateTemperature",
      },
    ],
    [
      initialData?.validateExpirety,
      initialData?.validateMarking,
      initialData?.validatePackaging,
      initialData?.validateQuality,
      initialData?.validateTemperature,
    ],
  );
  const translatedGoodsTemperaturesDryFood = goodsTemperaturesDryFood(t);

  const temps: Record<
    string,
    {
      value: string;
      label: string;
      color: AcceptenceCriteriaColors;
    }[]
  > = {
    "1": goodsTemperaturesCool,
    "2": goodsTemperaturesFrost,
    "3": goodsTemperaturesFish,
    "4": translatedGoodsTemperaturesDryFood,
  };

  const checkBoxTexts: string[] = [
    t("food.Labeling"),
    t("food.Shelf Life"),
    t("food.Undamaged Packaging"),
    t("food.Visual Quality Inspection"),
    t("food.Temperature Check"),
  ];

  const typeOfProductOptions: Option[] = [
    { label: t("food.Cold Storage Product"), value: 1 },
    { label: t("food.Freezer Product"), value: 2 },
    { label: t("food.Fresh Fish Product"), value: 3 },
    { label: t("food.Dry Food"), value: 4 },
  ];

  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [isAddGoodModalOpen, setIsAddGoodModalOpen] = useState(false);
  const addPestControlSchema = z.object({
    typeOfTheProduct: z.string().min(1, t("food.Type is Required")),
    temperature: z.string().min(1, t("food.Temperature is Required")),
    supplierId: z.string().min(1, t("food.Supplier is Required")),
    goodsName: z.string().min(1, t("food.Product Name is Required")),
    remarks: z
      .string()
      .optional()
      .refine((e) => !isRemarksNeeded, {
        message: t("food.Remarks are mandatory"),
      }),
    correctiveAction: z
      .boolean()
      .optional()
      .refine(
        (e) =>
          isCorrectiveActionNeeded || isChekmarksOk
            ? !!numberOfCorrectiveActionSelected.current
            : true,
        {
          message: t("food.At leat one corrective action needed"),
        },
      ),
  });
  type AddPestControlData = z.infer<typeof addPestControlSchema>;
  function processValidationNumbers(checkBoxStates: CheckboxStateValue[]) {
    const valueToNumberMap: { [key: string]: number } = {
      yes: 1,
      partial: 2,
      no: 3,
    };

    const result: {
      ValidateMarking: number;
      ValidateExpirety: number;
      ValidatePackaging: number;
      ValidateQuality: number;
      ValidateTemperature: number;
    } = {
      ValidateMarking: 1,
      ValidateExpirety: 1,
      ValidatePackaging: 1,
      ValidateQuality: 1,
      ValidateTemperature: 1,
    };

    checkBoxStates.forEach((state) => {
      const numberValue = valueToNumberMap[state.value as string];
      result[state.stateName as keyof typeof result] = numberValue;
    });

    return result;
  }

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
  } = useForm<AddPestControlData>({
    resolver: zodResolver(addPestControlSchema),
  });

  const handleOnClose = () => {
    reset();
    setIsCorrectiveActionNeeded(false);
    setIsRemarksNeeded(false);
    setNote([]);
    setTemperatureOptions([]);
    setSelectedTemperature(undefined);
    numberOfCorrectiveActionSelected.current = 0;
    setCheckBoxStates(checkBoxData);
    props.onClose?.();
  };

  const updateMutation = useMutation({
    mutationFn: updateItem,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Food Acceptence Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("food.Unable to add Food Acceptance"));
    },
  });

  const mutation = useMutation({
    mutationFn: addItem,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Food Acceptence Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("food.Unable to add Food Acceptance"));
    },
  });

  const noteTexts = {
    note1: t("food.The goods are assessed"),
    note2: t("food.The goods are discarded"),
    note3: t("food.The goods are returned"),
    note4: t("food.The supplier is contacted"),
    note5: t("food.Other"),
  };

  const editNote = (e: React.ChangeEvent<HTMLInputElement>) => {
    const noteText = noteTexts[e.target.name as keyof typeof noteTexts];
    const targetName = e.target.name;
    const isChecked = e.target.checked;
    isChecked
      ? ++numberOfCorrectiveActionSelected.current
      : --numberOfCorrectiveActionSelected.current;
    isChecked
      ? setNote((prev) => [...prev, noteText])
      : setNote((prev) => prev.filter((note) => note !== noteText));
    if (targetName === "note5" || targetName === "note1") {
      setIsRemarksNeeded(isChecked);
    }
  };
  const handleAction = (e: CheckboxStateValue) => {
    setCheckBoxStates((prevState) => {
      const newState = prevState.map((state) =>
        state.stateName === e.stateName ? { ...state, value: e.value } : state,
      );

      const isCorrectiveNeeded = newState.some(
        (state) => state.value === "partial" || state.value === "no",
      );

      setIsCheckMarksOk(isCorrectiveNeeded);

      return newState;
    });
  };

  useEffect(() => {
    if (checkBoxStates.length === 0) {
      setCheckBoxStates(checkBoxData);
    }

    if (initialData && initialData.temperature) {
      const { remark, reason } = parseRemarkAndCorrectiveActions(
        initialData.note,
      );
      setSelectedTemperature(
        temperatureOptions?.find(
          (h) => h.value === `${initialData.temperature}`,
        ),
      );
      setNote(reason);
      setIsCheckMarksOk(
        checkBoxStates.some(
          (state) => state.value === "partial" || state.value === "no",
        ),
      );
      setIsCorrectiveActionNeeded(
        temperatureOptions?.find(
          (h) => h.value === initialData.temperature.toString(),
        )?.color === AcceptenceCriteriaColors.NotAcceptable,
      );
      numberOfCorrectiveActionSelected.current = reason.length;
      setTypeOfProduct(initialData.goodType);
      if (temperatureOptions.length === 0) {
        setTemperatureOptions(temps[initialData.goodType]);
      }
      reset({
        typeOfTheProduct: initialData.goodType?.toString(),
        temperature: initialData.temperature?.toString(),
        supplierId: initialData.supplierId,
        goodsName: initialData.goodsName,
        remarks: remark,
      });
    }
    // we need to not render again temps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkBoxData, checkBoxStates, initialData, reset, temperatureOptions]);

  const onSubmit = (data: AddPestControlData) => {
    if (!user?.id) return;
    const validationNumbers = processValidationNumbers(checkBoxStates);

    const isCompeletedWithErrors = isCorrectiveActionNeeded || isChekmarksOk;

    const payload = {
      companyId: foodControlPointInstance.companyId,
      companyLocationId: foodControlPointInstance.companyLocationId,
      reason: JSON.stringify(note),
      remark: data.remarks,
      controlPointInstanceId: foodControlPointInstance.id,
      foodControlPointInstance,
      status: isCompeletedWithErrors
        ? InstanceStatus.completedWithErrors
        : InstanceStatus.completed,
      validateMarking: Number(validationNumbers.ValidateMarking),
      validateExpirety: Number(validationNumbers.ValidateExpirety),
      validatePackaging: Number(validationNumbers.ValidatePackaging),
      validateQuality: Number(validationNumbers.ValidateQuality),
      validateTemperature: Number(validationNumbers.ValidateTemperature),
      temperature: Number(selectedTemperature?.value),
      goodsName: data.goodsName,
      supplierId: data.supplierId,
      registeredBy: user?.id,
      goodType: typeOfProduct,
      file: "",
    };

    if (initialData) {
      updateMutation.mutate(payload);
      return;
    }

    mutation.mutate(payload);
  };

  return (
    <>
      <Modal
        actionButtons={
          <>
            <Button
              variant="secondary"
              onClick={handleOnClose}
              disabled={mutation.isPending}
            >
              {t("common.Discard")}
            </Button>
            <Button
              loading={mutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              {t("food.Submit Entry")}
            </Button>
          </>
        }
        {...props}
        title={t("food.Food Acceptance")}
      >
        <form>
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Type of the Product")} required>
              <Select
                placeholder={t("common.Select an option")}
                {...register("typeOfTheProduct")}
                options={typeOfProductOptions}
                onChange={(e) => {
                  setTypeOfProduct(e.target.value);
                  setTemperatureOptions(temps[e.target.value]);
                }}
                defaultValue={Number(initialData?.goodType)}
              />
            </Label>
            <ErrorMessage message={errors.typeOfTheProduct?.message} />
          </div>
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Name of the Product")} required>
              <GoodSelection
                companyId={foodControlPointInstance.companyId}
                onAddNew={() => setIsAddGoodModalOpen(true)}
                onChange={(e) => setValue("goodsName", e.target.name)}
                defaultValue={initialData?.goodsName}
              />
            </Label>
            <ErrorMessage message={errors.goodsName?.message} />
          </div>
          <div className="flex flex-col items-start w-full gap-2">
            <Label title={t("food.Supplier")} required>
              <SupplierSelection
                companyId={foodControlPointInstance.companyId}
                onAddNew={() => setIsAddSupplierModalOpen(true)}
                onChange={(e) => setValue("supplierId", e.target.value)}
                defaultValue={initialData?.supplierId}
              />
            </Label>
            <ErrorMessage message={errors.supplierId?.message} />
          </div>
          <div className="border-b border-b-gray-200 my-2.5"></div>
          <div className="flex flex-col gap-y-6">
            {checkBoxTexts.map((text, index) => {
              return (
                <div
                  key={checkBoxData[index].stateName + "_" + index}
                  className="flex flex-row items-center gap-x-3"
                >
                  <label className="form-label !font-normal">{text}</label>
                  <div className="flex flex-col gap-2">
                    <MultiCheckbox
                      stateName={checkBoxData[index].stateName}
                      onChange={(e) => handleAction(e)}
                      value={
                        checkBoxStates.find(
                          (state) =>
                            state.stateName === checkBoxData[index].stateName,
                        )?.value
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-b border-b-gray-200 my-2.5"></div>
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Temperature")} required>
              <Select
                placeholder={t("food.Select Temperature")}
                {...register("temperature")}
                options={temperatureOptions}
                defaultValue={initialData?.temperature?.toString()}
                onChange={(e) => {
                  setSelectedTemperature(
                    temperatureOptions.find((h) => h.value === e.target.value),
                  );
                  setIsCorrectiveActionNeeded(
                    temperatureOptions.find((h) => h.value === e.target.value)
                      ?.color === AcceptenceCriteriaColors.NotAcceptable,
                  );
                  setValue("temperature", e.target.value);
                }}
              />
            </Label>
            <ErrorMessage message={errors.temperature?.message} />
          </div>

          {(isCorrectiveActionNeeded || isChekmarksOk) && (
            <>
              <div className="border-b border-b-gray-200 my-2.5"></div>
              <Label required title={t("food.Corrective Action")}>
                <div className="flex flex-col w-full gap-2 ">
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      name="note1"
                      className="checkbox checkbox-md"
                      defaultChecked={note.includes(noteTexts.note1)}
                      type="checkbox"
                      onChange={(e) => editNote(e)}
                    />
                    {noteTexts.note1}
                  </div>
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      onChange={(e) => editNote(e)}
                      className="checkbox checkbox-md"
                      defaultChecked={note.includes(noteTexts.note2)}
                      type="checkbox"
                      name="note2"
                    />
                    {noteTexts.note2}
                  </div>
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      name="note3"
                      defaultChecked={note.includes(noteTexts.note3)}
                      className="checkbox checkbox-md"
                      type="checkbox"
                      onChange={(e) => editNote(e)}
                    />
                    {noteTexts.note3}
                  </div>
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      name="note4"
                      defaultChecked={note.includes(noteTexts.note4)}
                      className="checkbox checkbox-md"
                      type="checkbox"
                      onChange={(e) => editNote(e)}
                    />
                    {noteTexts.note4}
                  </div>
                  <div className="flex flex-row gap-4 items-center">
                    <input
                      name="note5"
                      defaultChecked={note.includes(noteTexts.note5)}
                      className="checkbox checkbox-md"
                      type="checkbox"
                      onChange={(e) => editNote(e)}
                    />
                    {noteTexts.note5}
                  </div>
                  <ErrorMessage message={errors?.correctiveAction?.message} />
                </div>
              </Label>
            </>
          )}
          <div className="border-b border-b-gray-200 my-2.5"></div>
          <Label title={t("food.Remarks")}>
            <div className="flex flex-col items-end w-full gap-2 ">
              <Input
                {...register("remarks")}
                showRequired={isRemarksNeeded}
                placeholder="-"
                onChange={(e) => {
                  setIsRemarksNeeded(!e.target.value);
                }}
              />
              <ErrorMessage message={errors?.remarks?.message} />
            </div>
          </Label>
        </form>
      </Modal>
      <UpsertAcceptanceProductModal
        open={isAddGoodModalOpen}
        onClose={() => setIsAddGoodModalOpen(false)}
        companyId={foodControlPointInstance.companyId}
        selectedLocationId={foodControlPointInstance.companyLocationId}
        type={UpsertModalType.Add}
      />
      <UpsertSupplierModal
        open={isAddSupplierModalOpen}
        onClose={() => setIsAddSupplierModalOpen(false)}
        companyId={foodControlPointInstance.companyId}
        type={SupplierModalAction.ADD}
      />
    </>
  );
}
